import { useCallback } from 'preact/hooks';

import type { AdStartedParam } from '../../../types/global';
import {
  ButtonTarget,
  createCastSessionEndedTrackEvent,
  createCastSessionStartedTrackEvent,
  createChromecastSessionStartingTrackEvent,
  createClickTrackEvent,
  createViewErrorEvent,
  createViewAdEvent,
  createOriginDecorator,
} from '../services/tracking';

import { useCurrentAsset } from './CurrentAssetProvider';
import { usePulse } from './PulseProvider';
import { CastingType } from './CastProvider';
import { useProvider } from './useProvider';

export const useTracker = () => {
  const { configProvider: provider } = useProvider();
  const { asset } = useCurrentAsset();

  const pulse = usePulse();

  const trackView = useCallback(
    ({ name, elementType }: { name?: string; elementType: string }) => {
      return pulse.track(
        'trackerEvent',
        createOriginDecorator(
          provider,
          asset
        )({
          type: 'Engagement',
          intent: 'View',
          object: {
            id: elementType,
            type: 'UIElement',
            name,
            elementType,
          },
        })
      );
    },
    [provider, asset, pulse]
  );

  const trackClick = useCallback(
    ({ name, elementType }: { name?: string; elementType: string }) => {
      return pulse.track(
        'trackerEvent',
        createOriginDecorator(
          provider,
          asset
        )({
          type: 'Engagement',
          action: 'Click',
          object: {
            id: elementType,
            type: 'UIElement',
            name,
            elementType,
          },
        })
      );
    },
    [provider, asset, pulse]
  );

  const trackAdView = useCallback(() => {
    const trackEvent = createViewAdEvent(provider, asset);
    return pulse.track('viewEvent', trackEvent);
  }, [provider, asset, pulse]);

  const trackButtonClick = useCallback(
    (
      btn:
        | string
        | {
            elementIdentifier?: string;
            elementType?: string;
            title: string;
            intent?: string;
          },
      target?: ButtonTarget,
      sptCustom?: Partial<Record<string, unknown>>,
      objectType?: 'ad' | 'asset',
      adId?: string
    ) => {
      const button = typeof btn === 'string' ? { title: btn } : btn;

      const trackEvent = createClickTrackEvent(
        provider,
        asset ?? {},
        button,
        target,
        sptCustom,
        objectType,
        adId
      );

      return pulse.trackMediaEvent(trackEvent);
    },
    [provider, asset, pulse]
  );

  const trackNextUpClick = useCallback(
    (assetId: number) => {
      const target = {
        '@id': `sdrn:${provider}:mediaasset:${assetId}`,
        '@type': 'MediaAsset',
      };
      return trackButtonClick('next-up-asset', target);
    },
    [provider, trackButtonClick]
  );

  const trackAdLink = useCallback(
    (adData: AdStartedParam) => {
      const trackEvent = createClickTrackEvent(
        provider,
        asset ?? {},
        { title: 'ad-link' },
        {
          url:
            adData.meta.clickThroughUrl ||
            adData.meta.ima?.ad?.data?.clickThroughUrl,
        },
        {
          'spt:creativeId': adData.meta.creativeId,
        }
      );

      return pulse.trackMediaEvent(trackEvent);
    },
    [provider, asset, pulse]
  );

  const trackCastStarted = useCallback(
    (castingType: CastingType) => {
      const trackEvent = createCastSessionStartedTrackEvent({
        provider,
        asset,
        castingType,
      });

      return pulse.trackMediaEvent(trackEvent);
    },
    [provider, asset, pulse]
  );

  const trackCastEnded = useCallback(
    (castingType: CastingType, duration: number) => {
      const trackEvent = createCastSessionEndedTrackEvent({
        castingType,
        provider,
        asset,
        duration,
      });

      return pulse.trackMediaEvent(trackEvent);
    },
    [provider, asset, pulse]
  );

  const trackChromecastStarting = useCallback(() => {
    const trackEvent = createChromecastSessionStartingTrackEvent({
      provider,
      asset,
    });

    return pulse.trackMediaEvent(trackEvent);
  }, [provider, asset, pulse]);

  const trackChromecastStarted = useCallback(
    () => trackCastStarted('chromecast'),
    [trackCastStarted]
  );

  const trackChromecastEnded = useCallback(
    (duration: number) => trackCastEnded('chromecast', duration),
    [trackCastEnded]
  );

  const trackAirplayStarted = useCallback(
    () => trackCastStarted('airPlay'),
    [trackCastStarted]
  );

  const trackAirplayEnded = useCallback(
    (duration: number) => trackCastEnded('airPlay', duration),
    [trackCastEnded]
  );

  const trackErrorView = useCallback(
    (errorCode?: number) => {
      const trackEvent = createViewErrorEvent(provider, asset, errorCode);
      return pulse.track('viewEvent', trackEvent);
    },
    [provider, asset, pulse]
  );

  return {
    trackButtonClick,
    trackNextUpClick,
    trackAdLink,
    trackAdView,
    trackErrorView,
    trackChromecastStarting,
    trackChromecastStarted,
    trackChromecastEnded,
    trackAirplayStarted,
    trackAirplayEnded,
    trackView,
    trackClick,
  };
};
